import { motion } from "framer-motion";
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip } from "react-leaflet";
import { MapPin, Phone } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { useSiteContent, useSiteCopy, phoneHref } from "@/lib/siteContent";

const CENTER = [42.12, -88.42];

const towns = [
  { name: "Crystal Lake", dir: "left", pos: [42.2411, -88.3162] },
  { name: "Algonquin", hover: true, pos: [42.1656, -88.2943] },
  { name: "Lake in the Hills", hover: true, pos: [42.1817, -88.3304] },
  { name: "McHenry", dir: "right", pos: [42.3333, -88.2668] },
  { name: "Cary", hover: true, pos: [42.2119, -88.2381] },
  { name: "Huntley", hover: true, pos: [42.1681, -88.4281] },
  { name: "Woodstock", pos: [42.3147, -88.4487] },
  { name: "Marengo", hover: true, pos: [42.2486, -88.6084] },
  { name: "Fox Lake", hover: true, pos: [42.3967, -88.1837] },
  { name: "Johnsburg", hover: true, pos: [42.3789, -88.242] },
  { name: "Harvard", pos: [42.4222, -88.6137] },
  { name: "Rockford", dir: "bottom", pos: [42.2711, -89.094] },
  { name: "Belvidere", hover: true, pos: [42.2639, -88.8443] },
  { name: "Elgin", dir: "left", pos: [42.0354, -88.2826] },
  { name: "Schaumburg", dir: "bottom", pos: [42.0334, -88.0834] },
  { name: "Arlington Heights", hover: true, pos: [42.0884, -87.9806] },
  { name: "Evanston", pos: [42.0451, -87.6877] },
  { name: "Waukegan", pos: [42.3636, -87.8448] },
  { name: "Naperville", dir: "right", pos: [41.7508, -88.1535] },
  { name: "Aurora", dir: "left", pos: [41.7606, -88.3201] },
  { name: "Oak Brook", hover: true, pos: [41.8398, -87.9528] },
  { name: "Joliet", pos: [41.525, -88.0817] },
  { name: "Orland Park", dir: "bottom", pos: [41.6303, -87.8539] },
];

export default function ServiceArea() {
  const { tx, href } = useSiteCopy();
  const { headings, owners, colors } = useSiteContent();
  return (
    <section id="areas" data-testid="service-area-section" className="py-24 md:py-32 bg-navy border-y border-slate-800/60 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.35fr] gap-14 items-center">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-4" data-testid="areas-eyebrow">{headings.areas.eyebrow}</p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-tight chrome-text leading-none">
            {headings.areas.title}
          </h2>
          <p className="mt-6 text-slate-400 font-light leading-relaxed">
            {headings.areas.description}
          </p>

          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="service-area-towns">
            {towns.map((t) => (
              <span
                key={t.name}
                className="flex items-center gap-1.5 text-xs font-medium text-slate-300 border border-slate-700 rounded-full px-3.5 py-1.5 hover:border-electric hover:text-electric transition-colors duration-300"
              >
                <MapPin className="w-3 h-3 text-electric" />
                {tx(`area-town-${t.name}`, t.name)}
              </span>
            ))}
          </div>

          <a
            href={href("areas-call", phoneHref(owners[0].phone))}
            data-testid="service-area-call"
            className="mt-8 inline-flex items-center gap-2 text-electric font-semibold hover:underline"
          >
            <Phone className="w-4 h-4" /> {tx("areas-call", "Not on the map? Call anyway — we likely cover you.")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -inset-3 border border-electric/20 rounded-3xl -translate-x-3 translate-y-3" aria-hidden="true" />
          <div className="relative rounded-3xl overflow-hidden border border-slate-700 glow-blue" data-testid="service-area-map">
            <MapContainer
              center={CENTER}
              zoom={8}
              scrollWheelZoom={false}
              style={{ height: "480px", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Circle
                center={CENTER}
                radius={78000}
                pathOptions={{ color: colors.accent, weight: 1.5, fillColor: colors.accent, fillOpacity: 0.06 }}
              />
              {towns.map((t) => (
                <CircleMarker
                  key={t.name}
                  center={t.pos}
                  radius={6}
                  pathOptions={{ color: colors.accent, weight: 2, fillColor: colors.accent, fillOpacity: 0.9 }}
                >
                  <Tooltip permanent={!t.hover} direction={t.dir || "top"} offset={t.dir === "bottom" ? [0, 8] : t.dir ? [0, 0] : [0, -8]} className="town-label">
                    {tx(`area-town-${t.name}`, t.name)}
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
